import React, { useEffect, useRef, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { apiCall } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import PatientSearchDropdown from './PatientSearchDropdown';

interface ProviderOption {
  id: string;
  name: string;
}

interface AppointmentTypeOption {
  id: string;
  name: string;
  default_duration_minutes?: number;
}

interface SessionOption {
  id: string;
  start_time: string;
  end_time: string;
  facility_name?: string;
  room_name?: string;
}

interface BookAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBooked: () => void;
  providers: ProviderOption[];
  defaultDate: Date;
  initialPatientId?: string;
  initialPatientName?: string;
  initialProviderId?: string;
  initialStartTime?: Date;
  editingAppointmentId?: string | null;
}

// toISOString() normalizes to UTC -- for any local time where the UTC
// offset crosses midnight (e.g. evenings in AEST), that silently shifts
// the date input to the wrong calendar day. Use local date parts instead.
const toDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toTimeInputValue = (date: Date) => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

const formatTime = (date: Date) =>
  date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });

// 5-minute steps up to an hour, then 15-minute steps up to the 4-hour cap.
const DURATION_OPTIONS: number[] = [
  ...Array.from({ length: 12 }, (_, i) => (i + 1) * 5),
  ...Array.from({ length: 12 }, (_, i) => 60 + (i + 1) * 15),
];

const formatDuration = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} hr${h > 1 ? 's' : ''}`;
  return `${h} hr${h > 1 ? 's' : ''} ${m} min`;
};

const snapToDurationOption = (minutes: number): number =>
  DURATION_OPTIONS.reduce((closest, option) => (
    Math.abs(option - minutes) < Math.abs(closest - minutes) ? option : closest
  ), DURATION_OPTIONS[0]);

// Combines the date input (YYYY-MM-DD) and time input (HH:MM) into a local Date.
const combineDateAndTime = (dateStr: string, timeStr: string): Date | null => {
  if (!dateStr || !timeStr) return null;
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hours, minutes] = timeStr.split(':').map(Number);
  if ([year, month, day, hours, minutes].some((n) => Number.isNaN(n))) return null;
  return new Date(year, month - 1, day, hours, minutes);
};

const BookAppointmentModal: React.FC<BookAppointmentModalProps> = ({
  isOpen,
  onClose,
  onBooked,
  providers,
  defaultDate,
  initialPatientId = '',
  initialPatientName = '',
  initialProviderId = '',
  initialStartTime,
  editingAppointmentId = null,
}) => {
  const { token } = useAuth();

  const [patientId, setPatientId] = useState('');
  const [patientName, setPatientName] = useState('');
  const [providerId, setProviderId] = useState('');
  const [date, setDate] = useState(toDateInputValue(defaultDate));
  const [sessions, setSessions] = useState<SessionOption[]>([]);
  const [sessionId, setSessionId] = useState('');
  const [startTime, setStartTime] = useState(''); // HH:MM, independent of session
  const [durationMinutes, setDurationMinutes] = useState(15);
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentTypeOption[]>([]);
  const [appointmentTypeId, setAppointmentTypeId] = useState('');
  const [notes, setNotes] = useState('');
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingAppointment, setLoadingAppointment] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // loadAppointmentTypes (below) blanks appointmentTypeId every time
  // providerId changes -- including the change edit-mode itself triggers
  // when it sets the provider -- so the fetched appointment's type can't
  // be applied directly without racing that blank-out. Applied once the
  // appointment-types list actually finishes loading instead.
  const pendingAppointmentTypeIdRef = useRef<string | null>(null);
  // Session is optional metadata (facility/room) -- once sessions load for
  // the resolved provider+date, try to match whichever session actually
  // covers the already-set start time, purely to pre-select it. Never
  // blocks anything if no match/no sessions exist.
  const pendingSessionMatchRef = useRef(true);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);

    if (editingAppointmentId) {
      let cancelled = false;
      setLoadingAppointment(true);
      setSessions([]);
      setSessionId('');
      (async () => {
        try {
          const response = await apiCall(`/scheduling/appointments/${editingAppointmentId}/`, { method: 'GET' }, token || undefined);
          if (!response.ok) throw new Error('Failed to load appointment');
          const data = await response.json();
          if (cancelled) return;
          setPatientId(String(data.patient || ''));
          setPatientName(data.patient_name || '');
          setProviderId(String(data.provider || ''));
          const start = new Date(data.start_time);
          setDate(toDateInputValue(start));
          setStartTime(toTimeInputValue(start));
          setNotes(data.notes || '');
          pendingAppointmentTypeIdRef.current = data.appointment_type ? String(data.appointment_type) : null;
          const existingDuration = Math.round(
            (new Date(data.end_time).getTime() - start.getTime()) / 60000
          );
          setDurationMinutes(existingDuration > 0 ? Math.min(240, Math.max(5, existingDuration)) : 15);
          pendingSessionMatchRef.current = true;
        } catch {
          if (!cancelled) setError('Failed to load appointment details.');
        } finally {
          if (!cancelled) setLoadingAppointment(false);
        }
      })();
      return () => { cancelled = true; };
    }

    setPatientId(initialPatientId);
    setPatientName(initialPatientName);
    setProviderId(initialProviderId);
    const seedDate = initialStartTime || defaultDate;
    setDate(toDateInputValue(seedDate));
    setStartTime(initialStartTime ? toTimeInputValue(initialStartTime) : '');
    setSessions([]);
    setSessionId('');
    setDurationMinutes(15);
    setAppointmentTypeId('');
    setNotes('');
    pendingAppointmentTypeIdRef.current = null;
    pendingSessionMatchRef.current = true;
  }, [isOpen, editingAppointmentId, defaultDate, initialPatientId, initialPatientName, initialProviderId, initialStartTime]);

  // Sessions are optional (facility/room context) -- once they load, try to
  // pre-select whichever one covers the current start time. Purely cosmetic;
  // never blocks Start Time/Duration, which are always editable regardless.
  useEffect(() => {
    if (!isOpen || sessions.length === 0 || !pendingSessionMatchRef.current) return;
    const target = combineDateAndTime(date, startTime);
    if (target) {
      const matched = sessions.find((s) => target >= new Date(s.start_time) && target < new Date(s.end_time));
      if (matched) setSessionId(matched.id);
    }
    pendingSessionMatchRef.current = false;
  }, [isOpen, sessions, date, startTime]);

  useEffect(() => {
    if (!isOpen || !token || !providerId) {
      setAppointmentTypes([]);
      setAppointmentTypeId('');
      return;
    }

    const loadAppointmentTypes = async () => {
      setAppointmentTypeId('');
      try {
        const response = await apiCall(
          `/scheduling/appointment-types/?is_active=true&provider=${providerId}`,
          { method: 'GET' },
          token,
        );
        if (!response.ok) {
          setAppointmentTypes([]);
          return;
        }
        const data = await response.json();
        const list = (Array.isArray(data) ? data : data.results || []).map((type: any) => ({
          id: String(type.id),
          name: type.name,
          default_duration_minutes: type.default_duration_minutes,
        }));
        setAppointmentTypes(list);

        const pendingTypeId = pendingAppointmentTypeIdRef.current;
        if (pendingTypeId && list.some((t: AppointmentTypeOption) => t.id === pendingTypeId)) {
          setAppointmentTypeId(pendingTypeId);
        }
        pendingAppointmentTypeIdRef.current = null;
      } catch (err) {
        console.error('Error loading appointment types:', err);
        setAppointmentTypes([]);
      }
    };
    loadAppointmentTypes();
  }, [isOpen, token, providerId]);

  useEffect(() => {
    if (!isOpen || !token || !providerId || !date) {
      setSessions([]);
      setSessionId('');
      return;
    }

    const loadSessions = async () => {
      setLoadingSessions(true);
      setSessionId('');
      pendingSessionMatchRef.current = true;
      try {
        const response = await apiCall(
          `/providers/sessions/?provider=${providerId}&date_from=${date}&date_to=${date}`,
          { method: 'GET' },
          token,
        );
        if (!response.ok) {
          setSessions([]);
          return;
        }
        const data = await response.json();
        const list = (Array.isArray(data) ? data : data.results || []).map((session: any) => ({
          id: String(session.id),
          start_time: session.start_time,
          end_time: session.end_time,
          facility_name: session.facility_details?.name,
          room_name: session.room_details?.name || session.room_details?.full_name,
        }));
        setSessions(list);
      } catch (err) {
        console.error('Error loading provider sessions:', err);
        setSessions([]);
      } finally {
        setLoadingSessions(false);
      }
    };

    loadSessions();
  }, [isOpen, token, providerId, date]);

  if (!isOpen) return null;

  const selectedAppointmentType = appointmentTypes.find((t) => t.id === appointmentTypeId) || null;
  const startDateTime = combineDateAndTime(date, startTime);
  const endDateTime = startDateTime ? new Date(startDateTime.getTime() + durationMinutes * 60000) : null;

  const handleSubmit = async () => {
    if (!patientId || !startDateTime || !appointmentTypeId) {
      setError('Please fill in patient, start time, and appointment type.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        patient: patientId,
        provider: providerId,
        appointment_type: appointmentTypeId,
        title: `${patientName} - ${selectedAppointmentType?.name || 'Appointment'}`,
        start_time: startDateTime.toISOString(),
        end_time: (endDateTime as Date).toISOString(),
        notes,
      };
      if (sessionId) {
        payload.provider_session = sessionId;
      }
      const response = editingAppointmentId
        ? await apiCall(`/scheduling/appointments/${editingAppointmentId}/`, {
            method: 'PATCH',
            body: JSON.stringify(payload),
          }, token || undefined)
        : await apiCall('/scheduling/appointments/', {
            method: 'POST',
            body: JSON.stringify(payload),
          }, token || undefined);

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.detail || body.error || 'Failed to save appointment');
      }

      onBooked();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save appointment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-2xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg">{editingAppointmentId ? 'Edit Appointment' : 'New Appointment'}</h3>
          <button className="btn btn-sm btn-circle btn-ghost" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {error && (
          <div className="alert alert-error mb-4">
            <span>{error}</span>
          </div>
        )}

        {loadingAppointment && (
          <div className="flex justify-center py-8">
            <span className="loading loading-spinner loading-md"></span>
          </div>
        )}

        {!loadingAppointment && (
        <div className="space-y-4">
          <div className="form-control">
            <label className="label"><span className="label-text">Patient *</span></label>
            <PatientSearchDropdown
              value={patientName}
              onChange={(id, name) => {
                setPatientId(id);
                setPatientName(name);
              }}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="form-control">
              <label className="label"><span className="label-text">Provider *</span></label>
              <select
                className="select select-bordered w-full"
                value={providerId}
                onChange={(e) => setProviderId(e.target.value)}
              >
                <option value="">Select provider</option>
                {providers.map((provider) => (
                  <option key={provider.id} value={provider.id}>{provider.name}</option>
                ))}
              </select>
            </div>
            <div className="form-control">
              <label className="label"><span className="label-text">Date *</span></label>
              <input
                type="date"
                className="input input-bordered w-full"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="form-control">
              <label className="label"><span className="label-text">Start Time *</span></label>
              <input
                type="time"
                className="input input-bordered w-full"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div className="form-control">
              <label className="label"><span className="label-text">Duration *</span></label>
              <select
                className="select select-bordered w-full"
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(Number(e.target.value))}
              >
                {DURATION_OPTIONS.map((minutes) => (
                  <option key={minutes} value={minutes}>{formatDuration(minutes)}</option>
                ))}
              </select>
            </div>
          </div>

          {endDateTime && (
            <div className="text-sm opacity-70">
              Ends at {formatTime(endDateTime)}
            </div>
          )}

          <div className="form-control">
            <label className="label"><span className="label-text">Session / Room (optional)</span></label>
            {loadingSessions ? (
              <div className="text-sm opacity-70">Loading available sessions...</div>
            ) : !providerId ? (
              <div className="text-sm opacity-70">Select a provider and date first.</div>
            ) : sessions.length === 0 ? (
              <div className="text-sm opacity-70">No rostered session for this provider on this date.</div>
            ) : (
              <select
                className="select select-bordered w-full"
                value={sessionId}
                onChange={(e) => setSessionId(e.target.value)}
              >
                <option value="">No session / room selected</option>
                {sessions.map((session) => (
                  <option key={session.id} value={session.id}>
                    {formatTime(new Date(session.start_time))} - {formatTime(new Date(session.end_time))}
                    {session.facility_name ? ` — ${session.facility_name}` : ''}
                    {session.room_name ? ` (${session.room_name})` : ' (no room assigned)'}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="form-control">
            <label className="label"><span className="label-text">Appointment Type *</span></label>
            {!providerId ? (
              <div className="text-sm opacity-70">Select a provider first.</div>
            ) : appointmentTypes.length === 0 ? (
              <div className="text-sm opacity-70">No appointment types configured for this provider.</div>
            ) : (
              <select
                className="select select-bordered w-full"
                value={appointmentTypeId}
                onChange={(e) => {
                  const newId = e.target.value;
                  setAppointmentTypeId(newId);
                  const type = appointmentTypes.find((t) => t.id === newId);
                  if (type?.default_duration_minutes) {
                    setDurationMinutes(snapToDurationOption(type.default_duration_minutes));
                  }
                }}
              >
                <option value="">Select appointment type</option>
                {appointmentTypes.map((type) => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            )}
          </div>

          <div className="form-control">
            <label className="label"><span className="label-text">Notes</span></label>
            <textarea
              className="textarea textarea-bordered w-full"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>
        </div>
        )}

        <div className="modal-action">
          <button className="btn btn-ghost" onClick={onClose} disabled={submitting}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loadingAppointment || submitting || !patientId || !startDateTime || !appointmentTypeId}
          >
            {submitting
              ? (editingAppointmentId ? 'Saving...' : 'Booking...')
              : (editingAppointmentId ? 'Save Changes' : 'Book Appointment')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookAppointmentModal;
